<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="Society Requests"
      subtitle="Review and manage pending society registration requests."
      :headers="headers"
      :items="societies"
      :loading="loading"
      :show-search="true"
      :search.sync="searchText"
      search-placeholder="Search societies..."
    >
      <!-- Name with Logo -->
      <template v-slot:item.name="{ item }">
        <div style="display:flex;align-items:center;gap:12px;">
          <PremiumAvatar
            :image="item.logo_url ? $resolveUploadUrl(item.logo_url) : null"
            :name="item.name"
            :size="32"
            variant="squircle"
          />
          <span style="font-weight:600;font-size:13.5px;color:var(--text-primary);">{{ item.name }}</span>
        </div>
      </template>

      <!-- Type column -->
      <template v-slot:item.type="{ item }">
        <span class="ism-badge" :class="item.type == 1 ? 'ism-badge-blue' : (item.type == 2 ? 'ism-badge-accent' : 'ism-badge-purple')">
          {{ item.type == 1 ? 'Residential' : (item.type == 2 ? 'Commercial' : 'Both') }}
        </span>
      </template>

      <!-- Address column -->
      <template v-slot:item.address="{ item }">
        <TruncatedCell
          :text="`${item.address}, ${item.cityData ? item.cityData.name : (item.other_city || item.city)}, ${item.stateData ? item.stateData.name : (item.other_state || item.state)}`"
          max-width="220px"
        />
      </template>

      <!-- Contact number -->
      <template v-slot:item.contact_person_number="{ item }">
        <span style="white-space:nowrap;font-size:13.5px;">
          {{ item.country_code ? (item.country_code.startsWith('+') ? item.country_code : '+' + item.country_code) : '' }} {{ item.contact_person_number }}
        </span>
      </template>

      <!-- Actions column -->
      <template v-slot:item.actions="{ item }">
        <div style="display:flex;gap:8px;align-items:center;">
          <!-- View -->
          <PremiumActionButton
            :to="'/superadmin/society/requests/' + item.id + '/details'"
            icon="mdi-eye-outline"
            label="View"
            variant="info"
          />
          <!-- Accept -->
          <PremiumActionButton
            icon="mdi-check"
            label="Accept"
            variant="success"
            @click="openAccept(item)"
          />
          <!-- Reject -->
          <PremiumActionButton
            icon="mdi-close"
            label="Reject"
            variant="danger"
            @click="openReject(item)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- Accept Confirm Modal -->
    <ConfirmModal
      :isOpen="acceptDialog"
      type="success"
      title="Approve Society"
      :message="`Are you sure you want to approve ${pendingItem ? pendingItem.name : 'this society'}?`"
      confirmText="Yes, Approve"
      cancelText="Cancel"
      @confirm="acceptSociety"
      @cancel="closeDialogs"
    />

    <!-- Reject Confirm Modal -->
    <ConfirmModal
      :isOpen="rejectDialog"
      type="danger"
      title="Reject Society"
      :message="`Are you sure you want to reject ${pendingItem ? pendingItem.name : 'this society'}? This cannot be undone.`"
      confirmText="Yes, Reject"
      cancelText="Cancel"
      @confirm="rejectSociety"
      @cancel="closeDialogs"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { useFilterStore } from '@/pinia/filterStore';
import PremiumTable from '../../common/PremiumTable.vue';
import ConfirmModal from '../../common/ConfirmModal.vue';
import PremiumActionButton from '../../common/PremiumActionButton.vue';
import TruncatedCell from '../../common/TruncatedCell.vue';
import PremiumAvatar from '../../common/PremiumAvatar.vue';

export default {
  name: 'SocietyRequests',
  components: { PremiumTable, ConfirmModal, PremiumActionButton, TruncatedCell, PremiumAvatar },

  data() {
    return {
      loading: false,
      societies: [],
      searchText: '',
      pendingItem: null,
      acceptDialog: false,
      rejectDialog: false,

      API_BASE_URL: process.env.VUE_APP_API_URL || 'https://api-staging.ismartsociety.com/api',
      headers: [
        { text: 'Society Name',   value: 'name',                  width: '24%' },
        { text: 'Type',           value: 'type',                  width: '10%' },
        { text: 'Address',        value: 'address',               width: '26%' },
        { text: 'Contact Person', value: 'contact_person_name',   width: '16%' },
        { text: 'Contact No.',    value: 'contact_person_number', width: '14%', sortable: false },
        { text: 'Actions',        value: 'actions',               width: '10%', sortable: false },
      ],
    };
  },

  computed: {
    filterStore() { return useFilterStore(); }
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    getSocietyRequests() {
      this.loading = true;
      this.$axios.get('superadmin/society/requests').then(res => {
        this.loading = false;
        const data = res.data;
        if (data.status) {
          this.societies = data.socities || data.societies || data.data || [];
        } else if (Array.isArray(data)) {
          this.societies = data;
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message || 'Failed to load requests' });
        }
      }).catch(() => { this.loading = false; });
    },

    openAccept(item) {
      this.pendingItem = item;
      this.acceptDialog = true;
    },
    openReject(item) {
      this.pendingItem = item;
      this.rejectDialog = true;
    },
    closeDialogs() {
      this.acceptDialog = false;
      this.rejectDialog = false;
      this.pendingItem  = null;
    },

    acceptSociety() {
      const society = this.pendingItem;
      this.closeDialogs();
      this.$axios.post('superadmin/society/approve/' + society.id).then(res => {
        const data = res.data;
        if (data.status) {
          const idx = this.societies.indexOf(society);
          if (idx !== -1) this.societies.splice(idx, 1);
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: data.message });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message });
        }
      });
    },

    rejectSociety() {
      const society = this.pendingItem;
      this.closeDialogs();
      this.$axios.post('superadmin/society/reject/' + society.id).then(res => {
        const data = res.data;
        if (data.status) {
          const idx = this.societies.indexOf(society);
          if (idx !== -1) this.societies.splice(idx, 1);
          this.setAlert({ showAlert: true, variant: 'green accent-4', timeout: 3000, message: data.message });
        } else {
          this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: data.message });
        }
      });
    },
  },

  mounted() {
    this.getSocietyRequests();

    // ── Restore Filters ──
    this.filterStore.updateFilter('society_requests', {});
    const saved = this.filterStore.getFilter('society_requests');
    if (saved && saved.search) {
      this.searchText = saved.search;
    }
  },

  watch: {
    searchText(val) {
      this.filterStore.updateFilter('society_requests', { search: val });
    }
  }
};
</script>